// Batch area-ops ("Toplu Düzenleme") jobs store a numbered summary as their
// prompt — middleware area-ops-batch.summary.ts, one line per op:
//   1. [remove-object] sandalye
//   2. [change-image-text] "Merhaba" → "İstanbul"
//   3. [translate] → English
// Parsed back into rows so JobModal can show a list instead of the raw text.

export const AREA_OP_TYPES = [
  "replace-product",
  "remove-object",
  "remove-text",
  "translate",
  "change-image-text",
  "edit-region",
  "add-object",
  "add-logo",
] as const;

export type AreaOpType = (typeof AREA_OP_TYPES)[number];

export interface AreaOpRow {
  type: AreaOpType;
  text: string;
  // change-image-text: old → new text. translate: only `to` (the language).
  from?: string;
  to?: string;
}

const LINE = /^\d+\.\s+\[([a-z-]+)\]\s?(.*)$/;
const TEXT_CHANGE = /^"([\s\S]*)" → "([\s\S]*)"$/;
const TRANSLATE = /^→\s*([\s\S]*)$/;

const isAreaOpType = (s: string): s is AreaOpType =>
  (AREA_OP_TYPES as readonly string[]).includes(s);

// null unless every op line is a known area op — any other prompt stays raw.
// A line without the "N. [type]" head continues the previous op (a user prompt
// that had a line break in it).
export function parseAreaOpsPrompt(prompt?: string | null): AreaOpRow[] | null {
  const lines = (prompt || "").split(/\r?\n/);
  const ops: { type: AreaOpType; text: string }[] = [];
  for (const line of lines) {
    const m = LINE.exec(line.trim());
    if (m) {
      if (!isAreaOpType(m[1])) return null;
      ops.push({ type: m[1], text: m[2].trim() });
    } else if (ops.length) {
      ops[ops.length - 1].text += `\n${line}`;
    } else if (line.trim()) {
      return null;
    }
  }
  if (!ops.length) return null;

  return ops.map(({ type, text }) => {
    const body = text.trim();
    const change = type === "change-image-text" ? TEXT_CHANGE.exec(body) : null;
    if (change) return { type, text: body, from: change[1], to: change[2] };
    const lang = type === "translate" ? TRANSLATE.exec(body) : null;
    if (lang) return { type, text: body, to: lang[1].trim() };
    return { type, text: body };
  });
}
